import { CSP_NONCE, DOCUMENT, inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { deepEqual as equal } from 'fast-equals';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { StoreId, StoreType } from 'src/app/models';
import { environment } from 'src/environments/environment';
import { ContentService } from '../content/content.service';
import { CookiePolicyService } from '../cookie/cookie.service';
import { GtmService } from './gtm.service';
import { SecurityService } from '../security/security.service';

const GOOGLE_TAGS = {
  NXT_GLOBAL: 'GTM-NQVXL63S',
  MENY: 'GTM-WV3KKB8',
  CITY_CATERING: 'GTM-WWTG54Q',
  LA_BRAVA: 'GTM-K28LNSK',
  HERLIGHEDER: 'GTM-TVFPKC7',
  KOKKEFROEEN: 'GTM-TPWHZ8D',
  MK_EVENT: 'GTM-5PLP476',
  SPAR_EVENT: 'GTM-WNLL6J5',
  FANDANKO: 'GTM-PQ4K6RK',
  GASTROLUX: 'GTM-P27QTFPB',
  SKYTTEHUSET: 'GTM-5DVQW79S',
  KOKKERIET_SVENSSON: 'GTM-5KV45Q98',
  WIKA_MENY: 'GTM-KR5DF73Q',
  DINING_SIX: 'GTM-N5JNW3D',
  MENY_SALTUM: 'GTM-M87V8J8P',
  MENY_VIBY: 'GTM-WZHCKSSN',
  MENY_FJERRITSLEV: 'GTM-TWB7B25C',
  MENY_SVOGERSLEV: 'GTM-W2M48CZL',
  MENY_AARUP: 'GTM-WSDVZ5W6',
  MENY_BOGENSE: 'GTM-NMRJK3JD',
  MENY_THORSOE: 'GTM-MMHB9ZDN',
  MENY_HADSUND: 'GTM-NLBD3X8J',
  MENY_PARKVEJ: 'GTM-NMRJK3JD',
};

const GA4_TAGS = {
  MENY_HADSUND: 'G-QW4YZTXP9K',
  MENY_PARKVEJ: 'G-NQ7T533Z3M',
};

const TAG_KEY = '{TAG}';
const gTagScript = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}

  // Default ad_storage to 'denied'.
  gtag('consent', 'default', {
    'ad_storage': 'denied',
    'analytics_storage': 'denied',
    'functionality_storage': 'denied',
    'personalization_storage': 'denied',
    'security_storage': 'granted',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'wait_for_update': 2000,
  });
`;

const TAG_HEADER_FORMAT = `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${TAG_KEY}');
`;

const TAG_BODY_FORMAT = `
  <iframe src="https://www.googletagmanager.com/ns.html?id=${TAG_KEY}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
`;

const ANALYTICS_HEADER_FORMAT = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}

  gtag('js', new Date());
  gtag('config', '${TAG_KEY}');
`;

const TAG_HEADER_DINING_DIX = `
  !function () {
      "use strict";
      function l(e) {
          for (var t = e, r = 0, n = document.cookie.split(";"); r < n.length; r++) {
              var o = n[r].split("=");
              if (o[0].trim() === t)
                  return o[1]
          }
      }
      function s(e) {
          return localStorage.getItem(e)
      }
      function u(e) {
          return window[e]
      }
      function A(e, t) {
          e = document.querySelector(e);
          return t ? null == e ? void 0 : e.getAttribute(t) : null == e ? void 0 : e.textContent
      }
      var e = window,
      t = document,
      r = "script",
      n = "dataLayer",
      o = "https://dfdghrgb.diningsix.dk",
      a = "https://load.dfdghrgb.diningsix.dk",
      i = "72odfdghrgb",
      c = "2bd=CwNPLzMpSypXLTw%2FVCBAEl9VXFlBAAhZBg4cAQkDFQ0aSRYD",
      g = "cookie",
      v = "user_id",
      E = "",
      d = !1;
      try {
          var d = !!g && (m = navigator.userAgent, !!(m = new RegExp("Version/([0-9._]+)(.*Mobile)?.*Safari.*").exec(m))) && 16.4 <= parseFloat(m[1]),
          f = "stapeUserId" === g,
          I = d && !f ? function (e, t, r) {
              void 0 === t && (t = "");
              var n = {
                  cookie: l,
                  localStorage: s,
                  jsVariable: u,
                  cssSelector: A
              },
              t = Array.isArray(t) ? t : [t];
              if (e && n[e])
                  for (var o = n[e], a = 0, i = t; a < i.length; a++) {
                      var c = i[a],
                      c = r ? o(c, r) : o(c);
                      if (c)
                          return c
                  }
              else
                  console.warn("invalid uid source", e)
          }
          (g, v, E) : void 0;
          d = d && (!!I || f)
      } catch (e) {
          console.error(e)
      }
      var m = e,
      g = (m[n] = m[n] || [], m[n].push({
              "gtm.start": (new Date).getTime(),
              event: "gtm.js"
          }), t.getElementsByTagName(r)[0]),
      v = I ? "&bi=" + encodeURIComponent(I) : "",
      E = t.createElement(r),
      f = (d && (i = 8 < i.length ? i.replace(/([a-z]{8}$)/, "kp$1") : "kp" + i), !d && a ? a : o);
      E.async = !0,
      E.src = f + "/" + i + ".js?" + c + v,
      null != (e = g.parentNode) && e.insertBefore(E, g)
  }
  ();
`;

const TAG_BODY_FORMAT_DINING_SIX = `
  <iframe src="https://load.dfdghrgb.diningsix.dk/ns.html?id=GTM-N5JNW3D"
   height="0" width="0" style="display:none;visibility:hidden"></iframe>
`;

@Injectable({ providedIn: 'root' })
export class AnalyticService implements OnDestroy {
  private document = inject<Document>(DOCUMENT);
  private rendererFactory = inject(RendererFactory2);
  private contentService = inject(ContentService);
  private cookiePolicyService = inject(CookiePolicyService);
  private gtmService = inject(GtmService);
  private router = inject(Router);
  private nonce = inject(CSP_NONCE);

  private subscription;

  private renderer: Renderer2;

  constructor() {
    this.subscription = new Subscription();
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  setGoogleAnalytics(type: StoreType) {
    if (!environment.analytics) return;

    let brandKey;
    switch (type) {
      case StoreType.MENY:
        brandKey = GOOGLE_TAGS.MENY;
        break;
      case StoreType.CITYCATERING:
        brandKey = GOOGLE_TAGS.CITY_CATERING;
        break;
      case StoreType.LA_BRAVA:
        brandKey = GOOGLE_TAGS.LA_BRAVA;
        break;
      case StoreType.HERLIGHEDER:
        brandKey = GOOGLE_TAGS.HERLIGHEDER;
        break;
      case StoreType.KOKKEFROEEN:
        brandKey = GOOGLE_TAGS.KOKKEFROEEN;
        break;
      case StoreType.FANDANKO:
        brandKey = GOOGLE_TAGS.FANDANKO;
        break;
      case StoreType.GASTROLUX:
        brandKey = GOOGLE_TAGS.GASTROLUX;
        break;
      case StoreType.SKYTTEHUSET:
        brandKey = GOOGLE_TAGS.SKYTTEHUSET;
        break;
      case StoreType.KOKKERIET_SVENSSON:
        brandKey = GOOGLE_TAGS.KOKKERIET_SVENSSON;
        break;
    }

    if (this.contentService.isEventStore) {
      switch (type) {
        case StoreType.SPAR:
          brandKey = GOOGLE_TAGS.SPAR_EVENT;
          break;
        case StoreType.MIN_KOBMAND:
          brandKey = GOOGLE_TAGS.MK_EVENT;
          break;
      }
    }

    let storeKey;
    let analyticsKey;
    const merchantId = this.contentService.getMerchantId();
    switch (merchantId) {
      case StoreId.WIKA_MENY:
        storeKey = GOOGLE_TAGS.WIKA_MENY;
        break;
      case StoreId.DINING_SIX_NYTAAR:
        storeKey = GOOGLE_TAGS.DINING_SIX;
        break;
      case StoreId.MENY_SALTUM:
        storeKey = GOOGLE_TAGS.MENY_SALTUM;
        break;
      case StoreId.MENY_VIBY:
        storeKey = GOOGLE_TAGS.MENY_VIBY;
        break;
      case StoreId.MENY_FJERRITSLEV:
        storeKey = GOOGLE_TAGS.MENY_FJERRITSLEV;
        break;
      case StoreId.MENY_SVOGERSLEV:
        storeKey = GOOGLE_TAGS.MENY_SVOGERSLEV;
        break;
      case StoreId.MENY_AARUP:
        storeKey = GOOGLE_TAGS.MENY_AARUP;
        break;
      case StoreId.MENY_BOGENSE:
        storeKey = GOOGLE_TAGS.MENY_BOGENSE;
        break;
      case StoreId.MENY_THORSOE:
        storeKey = GOOGLE_TAGS.MENY_THORSOE;
        break;
      case StoreId.MENY_HADSUND:
        storeKey = GOOGLE_TAGS.MENY_HADSUND;
        analyticsKey = GA4_TAGS.MENY_HADSUND;
        break;
      case StoreId.MENY_PARKVEJ:
        storeKey = GOOGLE_TAGS.MENY_PARKVEJ;
        analyticsKey = GA4_TAGS.MENY_PARKVEJ;
        break;
    }

    if (typeof gtag === 'undefined') {
      const head = this.document.getElementsByTagName('head')[0];
      const gTagElement = this.renderer.createElement('script') as HTMLScriptElement;
      gTagElement.nonce = this.nonce;
      gTagElement.innerHTML = gTagScript;
      head.appendChild(gTagElement);

      this.installGoogleTagManager(GOOGLE_TAGS.NXT_GLOBAL);
      if (brandKey) this.installGoogleTagManager(brandKey);
      if (storeKey) this.installGoogleTagManager(storeKey);
      if (type === StoreType.DININGSIX && merchantId !== StoreId.DINING_SIX_NYTAAR)
        this.installGoogleTagManager(null, TAG_HEADER_DINING_DIX, TAG_BODY_FORMAT_DINING_SIX);

      if (analyticsKey) this.installGoogleAnalytics(analyticsKey);
    }

    if (typeof window !== 'undefined' && window.dataLayer) {
      this.subscription.add(
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
          window.dataLayer.push({ event: 'pageview' });
        }),
      );

      this.subscription.add(
        this.cookiePolicyService.cookiePolicyChoice
          .pipe(
            skip(1),
            distinctUntilChanged((a, b) => {
              return equal(a, b);
            }),
          )
          .subscribe((cookie) => {
            if (!cookie) {
              return;
            }

            this.gtmService.updateConsent(cookie, this.cookiePolicyService.update);
          }),
      );
    }
  }

  private installGoogleTagManager(key: string, headerFormat = TAG_HEADER_FORMAT, bodyFormat = TAG_BODY_FORMAT) {
    const searchRegExp = new RegExp(TAG_KEY, 'g');
    const gHeaderTag = key ? headerFormat.replace(searchRegExp, key) : headerFormat;
    const gBodyTag = key ? bodyFormat.replace(searchRegExp, key) : bodyFormat;

    const head = this.document.getElementsByTagName('head')[0];
    const body = this.document.getElementsByTagName('body')[0];

    const gHeaderElement = this.renderer.createElement('script') as HTMLScriptElement;
    gHeaderElement.nonce = this.nonce;
    gHeaderElement.innerHTML = gHeaderTag;
    head.appendChild(gHeaderElement);

    const gBodyElement = this.renderer.createElement('noscript') as HTMLScriptElement;
    gBodyElement.nonce = this.nonce;
    gBodyElement.innerHTML = gBodyTag;
    body.insertBefore(gBodyElement, body.children[0]);
  }

  private installGoogleAnalytics(key: string, configFormat = ANALYTICS_HEADER_FORMAT) {
    const searchRegExp = new RegExp(TAG_KEY, 'g');
    const configTag = key ? configFormat.replace(searchRegExp, key) : configFormat;

    const head = this.document.getElementsByTagName('head')[0];

    const analyticLoadElement = this.renderer.createElement('script') as HTMLScriptElement;
    analyticLoadElement.nonce = this.nonce;
    analyticLoadElement.async = true;
    analyticLoadElement.src = key;
    head.appendChild(analyticLoadElement);

    const analyticConfigElement = this.renderer.createElement('script') as HTMLScriptElement;
    analyticConfigElement.nonce = this.nonce;
    analyticConfigElement.innerHTML = configTag;
    head.appendChild(analyticConfigElement);
  }

  public getClientId(): string {
    const cookie: Record<string, string> = {};
    document.cookie?.split(';').forEach(function (el) {
      const splitCookie = el.split('=');
      const key = splitCookie[0].trim();
      const value = splitCookie[1];
      cookie[key] = value;
    });
    const clientId = cookie['_ga']?.substring(6);
    return clientId;
  }
}
